import { navigate } from "@reach/router";
import { TabHeader } from "components/TabHeader";
import { Flex } from "@chakra-ui/react";
import { Button } from "newui/components/Button";
import { FiEdit } from "react-icons/fi";
import { cn } from "utils/helpers";
import { RFX_STATUS } from "utils/constants";
import {
	hasInviteeAcceptedRfx,
	hasInviteeSubmittedRfx,
} from "helpers/rfxHelper";
import { ResponsePdfDownloadButton } from "newui/features/Rfx/Submission/components/SubmissionToolbar/PdfDownloadButton";
import { SubmissionButtons } from "newui/features/Rfx/Submission/components/SubmissionToolbar/SubmissionButtons";
import FloatingButton from "newui/features/Rfx/components/FloatingButton";
import SkeletonBase from "newui/components/Skeleton";
import { SubmissionToolbarProps } from "./types";
import { AcceptDeclineButtons } from "./AcceptDeclineButtons";
import { getSlug } from "helpers/linkHelper";
import { useCurrentCompany } from "hooks/auth";
import BackButton from "newui/components/BackButton";
import { isRfxStatusMatch } from "newui/features/Rfx/utils";
import Tooltip from "components/Tooltip";

const SubmissionToolbar: React.FC<SubmissionToolbarProps> = ({
	continueTab,
	currentTab,
	hasDeadlinePassed,
	isBottom = false,
	isFileUploadInProgress,
	isClientView,
	isGuest,
	isLoading,
	isQuoteTabValid,
	isRFxClosed,
	loadingSave,
	rfxData,
	saveHandler,
	revokeSubmissionHandler,
	setShowDialog,
	submitHandler,
	submissionId,
	submissionStatus,
	validForm,
	onRequestRevision,
	onSubmitRevision,
	hasDraftRevision,
	pendingRevisionRequest,
}) => {
	const isInvited = submissionStatus === RFX_STATUS.INVITED;
	const hasAccepted = hasInviteeAcceptedRfx(submissionStatus);
	const hasSubmitted = hasInviteeSubmittedRfx(submissionStatus);
	const isDeclined = submissionStatus === RFX_STATUS.DECLINE;
	const isAwarded = submissionStatus === RFX_STATUS.AWARDED;
	const company = useCurrentCompany();
	const rfxId = rfxData?.id;
	const rfxName = rfxData?.name;
	const rfxStatus = rfxData?.status;
	const rfxHasQuote = rfxData?.rfxType?.show_quote && rfxData?.quoteType?.toLowerCase() !== "file";

	const isSupplierView = !isClientView;
	const isBeingRevised = submissionStatus === RFX_STATUS.BEING_REVISED;
	const showRequestRevisionButton = rfxHasQuote &&
		isSupplierView &&
		isRfxStatusMatch(rfxStatus, RFX_STATUS.EVALUATION) &&
		hasSubmitted &&
		!isAwarded &&
		!isBeingRevised &&
		pendingRevisionRequest?.status !== "approved";

	const isRevisionRequestPending = pendingRevisionRequest?.status === "pending";

	const handleBackButtonClick = () => {
		if (isClientView) {
			navigate(`/account/${company?.id}/rfx/${rfxId}/${getSlug(rfxName)}/all`);
			return;
		}

		if (isGuest) {
			navigate("/account/rfx");
			return;
		}

		navigate(`/account/${company?.id}/rfx`);
	};

	if (isLoading) {
		return (
			<div className="mt-4 flex justify-end gap-3">
				<SkeletonBase
					width="128px"
					height="46"
				/>
				<SkeletonBase
					width="128px"
					height="46"
				/>
			</div>
		);
	}

	return !isDeclined ? (
		<TabHeader
			noDivider={true}
			icon={
				!isBottom && (
					<BackButton handleBackButtonClick={handleBackButtonClick} />
				)
			}
		>
			{hasAccepted ? (
				<Flex
					gap={3}
					className={cn("items-center", isBottom && "py-8")}
					direction="column"
					align="stretch"
				>
					<Flex
						gap={3}
						className="items-center"
					>
						{validForm && (
							<ResponsePdfDownloadButton
								isLoading={loadingSave || isFileUploadInProgress}
								rfxId={rfxId}
								submissionId={submissionId}
							/>
						)}
						{!hasSubmitted && !hasDeadlinePassed && (
							<FloatingButton
								className="bottom-4"
								isDisabled={loadingSave || isFileUploadInProgress}
								isLoading={loadingSave || isFileUploadInProgress}
								loadingText={`${isFileUploadInProgress ? "File uploading" : "Saving"}, please wait...`}
								onClick={saveHandler}
								position="left"
								text="Save Progress"
							/>
						)}
						{showRequestRevisionButton && (
							<Tooltip
								label={
									isRevisionRequestPending
										? "Revision request pending"
										: undefined
								}
								isDisabled={!isRevisionRequestPending}
							>
								<Button
									btntype="secondary"
									disabled={isRevisionRequestPending}
									onClick={onRequestRevision}
									data-testid="request-revision-btn"
								>
									<FiEdit className="mr-2" /> Request Quote Revision
								</Button>
							</Tooltip>
						)}
						{isBeingRevised && hasDraftRevision && (
							<Button
								onClick={onSubmitRevision}
								data-testid="submit-revision-btn"
							>
								Submit Revision
							</Button>
						)}
						{!isRFxClosed && !isClientView && (
							<SubmissionButtons
								currentTab={currentTab}
								continueTab={continueTab}
								hasDeadlinePassed={hasDeadlinePassed}
								hasSubmitted={hasSubmitted}
								isLoading={loadingSave || isFileUploadInProgress}
								isQuoteTabValid={isQuoteTabValid}
								revokeSubmissionHandler={revokeSubmissionHandler}
								submitHandler={submitHandler}
								validForm={validForm}
							/>
						)}
					</Flex>
				</Flex>
			) : (
				isInvited && (
					<div className="align-start inline-flex justify-end gap-2">
						{!isBottom && (
							<span className="max-w-[350px] self-center text-sm">
								{hasDeadlinePassed ? (
									<span className="text-red-600">
										Deadline has passed, you can no longer accept or decline
									</span>
								) : (
									<>
										This is a Preview only, we can notify the Client and let
										them know if you Accept or Decline the Invitation.
									</>
								)}
							</span>
						)}
						<AcceptDeclineButtons
							className={isBottom ? "py-8" : "self-center"}
							hasDeadlinePassed={hasDeadlinePassed}
							isLoading={isLoading}
							setShowDialog={setShowDialog}
						/>
					</div>
				)
			)}
		</TabHeader>
	) : (
		!isBottom && (
			<TabHeader
				noDivider={true}
				icon={<BackButton handleBackButtonClick={() => navigate("..")} />}
			/>
		)
	);
};

export default SubmissionToolbar;
